import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelectUserContext } from '@innovyze/stylovyze';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ChatMessage } from '@/components/chat/';
import { FileUploadItem } from './FileUploadItem';
import { sendPrompt } from '@/services/chatApi';
import { uploadAttachment } from '@/services/attachmentsApi';
import {
  mockTablePrompt,
  mockImageCarouselPrompt,
} from '@/services/mockAPI/mockAPI';
import type {
  ChatMessage as ChatMessageType,
  FileUploadState,
  PromptAttachment,
} from '@/types/chat';
import { ScrollArea } from '../ui/scroll-area';
import { Sparkles, Paperclip, X, ArrowDown, ArrowUp, Plus } from 'lucide-react';
import { ArrowOutward } from '@mui/icons-material';
import { generatedId } from '@/utils/generateId';
import {
  extractChartMetadata,
  formatConversationHistory,
  createStreamingHandler,
} from '@/utils/chatUtils';
import { ChatHistory } from './ChatHistory';
import {
  fetchChatHistory,
  deleteChatConversation,
  clearAllChatHistory,
  fetchConversationMessages,
  transformToMessages,
} from '@/utils/chatHistoryUtils';
import type { Conversation } from '@/types/chat';
import {
  SUGGESTED_CATEGORIES,
  PROMPTS_BY_CATEGORY,
} from '@/constants/suggestedActions';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpEvent,
} from '@/services/analytics/adp';

export const ChatInterface = () => {
  const track = (
    eventName: string,
    attributes: Record<string, string> = {},
  ) => {
    trackAdpEvent(getBernoulliAdp(), eventName, attributes);
  };
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingStatus, setStreamingStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [fileUploads, setFileUploads] = useState<FileUploadState[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [conversationId, setConversationId] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const isProgrammaticScrollRef = useRef(false);
  const [chatHistory, setChatHistory] = useState<Conversation[]>([]);
  const [isRestoring, setIsRestoring] = useState(false);
  const uploadAbortControllersRef = useRef<Map<string, AbortController>>(
    new Map(),
  );
  const pendingRequestRef = useRef<{
    input: string;
    messagesLengthBefore: number;
  } | null>(null);
  const dragCounter = useRef(0);
  const isMountedRef = useRef(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const loadChatHistory = async () => {
      const conversations = await fetchChatHistory();
      setChatHistory(conversations);
    };

    loadChatHistory();
  }, []);

  const handleDeleteConversation = async (conversationIdToDelete: string) => {
    track(ADP_EVENTS.CHAT_HISTORY_DELETE, {
      conversation_id: conversationIdToDelete,
      route: window.location.pathname,
    });

    const success = await deleteChatConversation(conversationIdToDelete);
    if (success) {
      setChatHistory(prev =>
        prev.filter(conv => conv.conversation_id !== conversationIdToDelete),
      );
    }
  };

  const handleClearHistory = async () => {
    track(ADP_EVENTS.CHAT_HISTORY_CLEAR, {
      route: window.location.pathname,
    });

    const success = await clearAllChatHistory();
    if (success) {
      setChatHistory([]);
    }
  };

  const handleSelectConversation = async (selectedConversationId: string) => {
    track(ADP_EVENTS.CHAT_HISTORY_OPEN, {
      conversation_id: selectedConversationId,
      route: window.location.pathname,
    });

    try {
      setIsRestoring(true);
      setError(null);
      setMessages([]); // Clear current messages while loading

      const response = await fetchConversationMessages(selectedConversationId);
      if (response) {
        const restoredMessages = transformToMessages(response);
        setMessages(restoredMessages);
        setConversationId(selectedConversationId);
      } else {
        setError('Failed to load conversation');
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError('Failed to load conversation');
    } finally {
      setIsRestoring(false);
    }
  };

  // Auto-scroll only when enabled (user clicked scroll-to-bottom button)
  useEffect(() => {
    if (isAutoScrollEnabled && bottomRef.current) {
      isProgrammaticScrollRef.current = true;
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
      // Reset flag after animation completes (smooth scroll takes ~300-500ms)
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 500);
    }
  }, [messages, isLoading, isAutoScrollEnabled]);

  // Handle scroll events to show/hide scroll-to-bottom button
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    // Ignore scroll events triggered by programmatic scrolling
    if (isProgrammaticScrollRef.current) {
      return;
    }

    const target = event.currentTarget;
    const isAtBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 100;

    // Show button when not at bottom
    setShowScrollButton(!isAtBottom);

    // If user scrolled away from bottom, disable auto-scroll
    if (!isAtBottom) {
      setIsAutoScrollEnabled(false);
    }
  }, []);

  // Scroll to bottom and enable auto-scroll
  const handleScrollToBottom = useCallback(() => {
    isProgrammaticScrollRef.current = true;
    setIsAutoScrollEnabled(true);
    setShowScrollButton(false);
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Reset flag after animation completes
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 500);
  }, []);

  // Check if any files are currently uploading
  const isUploading = fileUploads.some(
    f => f.status === 'pending' || f.status === 'uploading',
  );

  // Get successfully uploaded files
  const completedUploads = fileUploads.filter(
    f => f.status === 'completed' && f.result,
  );

  // Start uploading a file immediately
  const startFileUpload = useCallback(async (file: File) => {
    const uploadId = generatedId();
    const abortController = new AbortController();
    uploadAbortControllersRef.current.set(uploadId, abortController);

    // Add to state as pending
    const initialState: FileUploadState = {
      id: uploadId,
      file,
      status: 'pending',
      progress: 0,
    };

    setFileUploads(prev => [...prev, initialState]);

    try {
      const result = await uploadAttachment(
        file,
        state => {
          if (!isMountedRef.current) return;
          setFileUploads(prev =>
            prev.map(f => (f.id === uploadId ? { ...f, ...state } : f)),
          );
        },
        abortController.signal,
      );

      if (!isMountedRef.current) return;

      setFileUploads(prev =>
        prev.map(f =>
          f.id === uploadId
            ? { ...f, status: 'completed', progress: 100, result }
            : f,
        ),
      );
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Upload was cancelled, remove from list
        setFileUploads(prev => prev.filter(f => f.id !== uploadId));
        return;
      }

      if (!isMountedRef.current) return;

      setFileUploads(prev =>
        prev.map(f =>
          f.id === uploadId
            ? {
                ...f,
                status: 'error',
                error: err instanceof Error ? err.message : 'Upload failed',
              }
            : f,
        ),
      );
    } finally {
      uploadAbortControllersRef.current.delete(uploadId);
    }
  }, []);

  // Handle new files - start uploading immediately
  const handleFilesAdded = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);

      // Start uploading each file immediately
      fileArray.forEach(file => {
        startFileUpload(file);
      });
    },
    [startFileUpload],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    handleFilesAdded(files);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (uploadId: string) => {
    // If still uploading, abort it
    const abortController = uploadAbortControllersRef.current.get(uploadId);
    if (abortController) {
      abortController.abort();
    }

    // Remove from state
    setFileUploads(prev => prev.filter(f => f.id !== uploadId));
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFilesAdded(files);
    }
  };

  const handleSendMessage = async () => {
    // Don't send if uploading or nothing to send
    if (isUploading) return;
    if (!input.trim() && completedUploads.length === 0) return;
    if (isLoading) return;

    setOpenCategoryId(null);

    let currentConversationId = conversationId;
    const isNewConversation = !currentConversationId;
    if (isNewConversation) {
      currentConversationId = generatedId();
      setConversationId(currentConversationId);
    }

    const requestId = generatedId();
    const currentInput = input.trim();
    track(ADP_EVENTS.CHAT_SEND_MESSAGE, {
      conversation_id: currentConversationId,
      has_attachments: String(completedUploads.length > 0),
      prompt_length: String(currentInput.length),
      route: window.location.pathname,
    });

    // Add new conversation to chat history immediately
    if (isNewConversation && currentInput) {
      const newConversation: Conversation = {
        conversation_id: currentConversationId,
        initial_prompt: currentInput,
        requested_at: new Date().toISOString(),
      };
      setChatHistory(prev => [newConversation, ...prev]);
    }
    const attachments: PromptAttachment[] = completedUploads.flatMap(f =>
      f.result
        ? [
            {
              filename: f.result.filename,
              s3_key: f.result.s3_key,
              size: f.result.size,
              headers: f.result.headers,
            },
          ]
        : [],
    );

    // Build user message content
    const userContent = currentInput || (attachments.length > 0 ? '' : '');

    const userMessage: ChatMessageType = {
      role: 'user',
      content: userContent,
      timestamp: new Date().toISOString(),
      request_id: requestId,
      extras:
        attachments.length > 0
          ? {
              files: attachments.map(a => ({
                filename: a.filename,
                size: a.size,
                s3_key: a.s3_key,
                headers: a.headers,
              })),
            }
          : undefined,
    };

    pendingRequestRef.current = {
      input: currentInput,
      messagesLengthBefore: messages.length,
    };

    setMessages(prev => [...prev, userMessage]);
    const baseLength = messages.length + 1;

    const chartMetadata = extractChartMetadata(messages);
    const conversationHistory = formatConversationHistory(messages);

    setInput('');
    setFileUploads([]);
    setIsLoading(true);
    setStreamingStatus('Thinking...');
    setError(null);
    // Re-enable auto-scroll when user sends a message
    setIsAutoScrollEnabled(true);
    const streamingMessageIndex = baseLength;
    const streamingMessage: ChatMessageType = {
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      request_id: requestId,
      blocks: [],
    };
    setMessages(prev => [...prev, streamingMessage]);

    abortControllerRef.current = new AbortController();

    try {
      // Check if user typed "table" to trigger mock table response
      if (currentInput.toLowerCase() === 'table') {
        const finalAssistant = await mockTablePrompt();
        if (isMountedRef.current) {
          setMessages(prev => {
            const updated = [...prev];
            if (updated[streamingMessageIndex]) {
              updated[streamingMessageIndex] = {
                ...finalAssistant,
                request_id: requestId,
              } as any;
            }
            return updated;
          });
        }
      } else if (currentInput.toLowerCase() === 'images') {
        const finalAssistant = await mockImageCarouselPrompt();
        if (isMountedRef.current) {
          setMessages(prev => {
            const updated = [...prev];
            if (updated[streamingMessageIndex]) {
              updated[streamingMessageIndex] = {
                ...finalAssistant,
                request_id: requestId,
              } as any;
            }
            return updated;
          });
        }
      } else {
        // Send prompt with attachments
        const promptRequest = {
          input: currentInput || 'Please analyze the attached files.',
          conversation_id: currentConversationId,
          request_id: requestId,
          conversation_history: conversationHistory,
          ...(attachments.length > 0 && { attachments }),
          extras:
            chartMetadata.length > 0
              ? { previous_charts: chartMetadata }
              : undefined,
        };

        const streamingHandler = createStreamingHandler(
          streamingMessageIndex,
          requestId,
          isMountedRef,
          setStreamingStatus,
          setMessages,
        );

        const finalAssistant = await sendPrompt(
          promptRequest,
          streamingHandler,
          abortControllerRef.current.signal,
        );

        if (isMountedRef.current) {
          setMessages(prev => {
            const updated = [...prev];
            if (updated[streamingMessageIndex]) {
              updated[streamingMessageIndex] = {
                ...finalAssistant,
                request_id: requestId,
              } as any;
            }
            return updated;
          });
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      console.error('Failed to send message:', err);
      if (!isMountedRef.current) return;

      setError(
        err instanceof Error
          ? err.message
          : 'Failed to send message. Please try again.',
      );

      const errorMessage: ChatMessageType = {
        role: 'assistant',
        content:
          'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[streamingMessageIndex] = errorMessage;
        return newMessages;
      });
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
        setStreamingStatus('');
        textareaRef.current?.focus();
        abortControllerRef.current = null;
        pendingRequestRef.current = null;
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewChat = () => {
    track(ADP_EVENTS.NAV_NEW_CHAT, {
      previous_conversation_id: conversationId || 'none',
      route: window.location.pathname,
    });

    setMessages([]);
    setError(null);
    setConversationId('');
    setOpenCategoryId(null);
  };

  const userContext = useSelectUserContext();
  const userDisplayName = userContext?.name?.trim()?.split(/\s+/)[0] ?? 'there';

  const handleToggleSidebar = useCallback(
    () => setSidebarOpen(prev => !prev),
    [],
  );

  const handleSelectPrompt = useCallback((prompt: string) => {
    setInput(prompt);
    textareaRef.current?.focus();
  }, []);

  const openCategory = openCategoryId
    ? SUGGESTED_CATEGORIES.find(c => c.id === openCategoryId)
    : null;
  const openCategoryPrompts = openCategoryId
    ? PROMPTS_BY_CATEGORY[openCategoryId] ?? []
    : [];

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;

      const pendingRequest = pendingRequestRef.current;
      pendingRequestRef.current = null;

      if (pendingRequest) {
        const { input, messagesLengthBefore } = pendingRequest;

        setInput(input);
        setMessages(prev => prev.slice(0, messagesLengthBefore));
      }

      setIsLoading(false);
      setStreamingStatus('');
    }
  };

  const isSendDisabled =
    isUploading ||
    isLoading ||
    (!input.trim() && completedUploads.length === 0);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="h-full w-full flex flex-col bg-gradient-to-br from-slate-50 via-slate-100 to-gray-100 overflow-hidden relative isolate"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>
      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-[#60DDFF]/10 backdrop-blur-sm">
          <div className="text-center p-8 rounded-2xl bg-white/90 border-2 border-[#007CA0] border-dashed shadow-xl">
            <Paperclip className="h-12 w-12 sm:h-14 sm:w-14 mx-auto mb-3 text-[#007CA0]" />
            <p className="text-lg sm:text-xl font-semibold text-[#252F33] mb-1">
              Drop files here
            </p>
            <p className="text-sm text-[#768D95]">All file types supported</p>
          </div>
        </div>
      )}
      {isRestoring && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="text-center p-8">
            <div className="relative mx-auto mb-4">
              <div className="w-12 h-12 rounded-full border-4 border-slate-200 border-t-[#007CA0] animate-spin" />
            </div>
            <p className="text-lg font-semibold text-[#252F33] mb-1">
              Loading Conversation
            </p>
            <p className="text-sm text-[#768D95]">
              Restoring messages and history...
            </p>
          </div>
        </div>
      )}
      <div className="flex h-full w-full bg-white">
        <ChatHistory
          conversations={chatHistory}
          isOpen={sidebarOpen}
          onToggle={handleToggleSidebar}
          onNewChat={handleNewChat}
          onSelectConversation={handleSelectConversation}
          onDeleteConversation={handleDeleteConversation}
          onClearHistory={handleClearHistory}
        />

        <div className="flex flex-col flex-1 min-w-0 bg-[#F9FAFC]">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            aria-hidden
          />
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 bg-[#F9FAFC] flex-shrink-0 relative border-b border-slate-200/60">
            <div className="flex items-center gap-2 bernoulli-landing-font">
              <Sparkles className="h-5 w-5 text-[#161618] flex-shrink-0" />
              <h1 className="text-[15px] font-medium text-[#161618] leading-tight">
                Bernoulli AI
              </h1>
            </div>

            {conversationId && (
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[50%] w-full">
                <div className="flex items-center gap-1 justify-center">
                  <span className="text-sm font-medium text-[#161618] truncate min-w-0">
                    {chatHistory.find(c => c.conversation_id === conversationId)
                      ?.initial_prompt ?? ''}
                  </span>
                </div>
              </div>
            )}

            <div className="w-20 sm:w-24" aria-hidden />
          </div>

          <div className="flex-1 overflow-hidden relative bg-[#F9FAFC]">
            {error && messages.length === 0 && (
              <div className="max-w-7xl mx-auto w-full px-3 sm:px-4 md:px-6 lg:px-8 pt-2 sm:pt-3 flex-shrink-0">
                <div className="p-3 sm:p-4 rounded-lg sm:rounded-xl bg-red-50 border border-[#E66B45] flex items-start justify-between gap-3">
                  <p className="text-sm text-[#E66B45] flex-1">{error}</p>
                  <button
                    onClick={() => setError(null)}
                    className="flex-shrink-0 text-[#E66B45] hover:text-[#d15a39] transition-colors"
                    aria-label="Close error message">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#60DDFF]/5 rounded-full blur-3xl" />
            </div>

            <ScrollArea
              className="h-full relative"
              viewportRef={scrollViewportRef}
              onScroll={handleScroll}>
              <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 min-h-full bg-[#F9FAFC]">
                {messages.length === 0 ? (
                  <div className="min-h-[calc(100vh-12rem)] w-full flex flex-col items-center pt-[18vh] pb-8">
                    <div className="w-full max-w-3xl bernoulli-landing-font">
                      <div className="flex flex-col items-start text-left mb-5">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#007CA0] flex-shrink-0">
                            <Sparkles className="h-4 w-4 text-white" />
                          </div>
                          <p className="text-[18px] font-normal text-[#161618] leading-tight tracking-tight">
                            Hi, {userDisplayName}
                          </p>
                        </div>
                        <h2 className="text-[22px] font-semibold text-[#161618] leading-tight tracking-tight">
                          How can I help you today?
                        </h2>
                      </div>

                      <div className="w-full mb-0">
                        <div className="rounded-2xl bg-[#FFFFFF] border border-slate-200 shadow-sm focus-within:border-slate-300 focus-within:ring-1 focus-within:ring-slate-200 transition-all overflow-hidden flex flex-col">
                          <div className="flex-1 min-h-[72px]">
                            <Textarea
                              ref={textareaRef}
                              value={input}
                              onChange={e => setInput(e.target.value)}
                              onKeyDown={handleKeyDown}
                              placeholder="Ask me anything..."
                              disabled={isLoading}
                              className="min-h-[72px] max-h-[200px] !border-0 !shadow-none focus-visible:ring-0 bg-transparent text-base font-normal px-4 pt-3 pb-1 rounded-none text-[#161618] placeholder:text-[#C1C6C7] placeholder:text-base placeholder:font-normal resize-none w-full bernoulli-landing-font"
                            />
                          </div>
                          <div className="flex items-center justify-between px-2 pb-2 pt-0 bg-[#FFFFFF] border-t-0">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={isLoading}
                              className="h-8 w-8 rounded-full bg-[#F9FAFC] hover:bg-[#F3F5F9] text-[#757576] flex-shrink-0 border-0 shadow-none">
                              <Plus className="h-4 w-4" />
                            </Button>
                            <div className="flex items-center gap-1 ml-auto">
                              {input.trim() && !isLoading && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setInput('');
                                    textareaRef.current?.focus();
                                  }}
                                  className="h-8 w-8 rounded-full text-[#757576] hover:text-[#252F33] hover:bg-[#F3F5F9] flex-shrink-0 border-0 shadow-none"
                                  aria-label="Clear input">
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                type="button"
                                onClick={
                                  isLoading ? handleCancel : handleSendMessage
                                }
                                disabled={isSendDisabled && !isLoading}
                                size="icon"
                                className={`h-8 w-8 rounded-full flex-shrink-0 border-0 shadow-none disabled:opacity-100 ${
                                  !isSendDisabled
                                    ? 'bg-[#7A8C94] hover:bg-[#6A7C84] text-white'
                                    : 'bg-[#F9FAFC] hover:bg-[#F3F5F9] text-[#7A8C94]'
                                }`}>
                                {isLoading ? (
                                  <X className="h-4 w-4" />
                                ) : (
                                  <ArrowUp className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                          {openCategoryId !== null && openCategory && (
                            <>
                              <div className="border-t border-slate-200" />
                              <div className="flex items-center justify-between px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <openCategory.icon
                                    className="h-4 w-4 text-[#768D95] flex-shrink-0"
                                    sx={{ fontSize: 18, color: '#768D95' }}
                                  />
                                  <span className="text-sm font-semibold text-[#252F33]">
                                    {openCategory.label}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setOpenCategoryId(null)}
                                  className="p-1.5 rounded-md text-[#768D95] hover:text-[#252F33] hover:bg-[#F3F5F9] transition-colors"
                                  aria-label="Close">
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                              <ul className="py-1">
                                {openCategoryPrompts.map((prompt, index) => (
                                  <li key={index}>
                                    <div className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#252F33]">
                                      <span className="flex-1 min-w-0 truncate">
                                        {prompt}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleSelectPrompt(prompt)
                                        }
                                        className="p-1.5 rounded-md text-[#768D95] hover:text-[#007CA0] hover:bg-[#F3F5F9] transition-colors flex-shrink-0"
                                        aria-label="Use this prompt">
                                        <ArrowOutward className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      </div>

                      {openCategoryId === null && (
                        <div className="flex flex-nowrap justify-center gap-2 pt-4">
                          {SUGGESTED_CATEGORIES.map(
                            ({ id, label, icon: Icon }) => (
                              <Button
                                key={id}
                                type="button"
                                variant="outline"
                                onClick={() => setOpenCategoryId(id)}
                                className="rounded-lg border border-slate-200 bg-[#F9FAFC] text-[#82949B] hover:bg-[#F3F5F9] hover:border-slate-300 text-sm font-normal py-1.5 px-3 h-auto flex-shrink-0 bernoulli-landing-font">
                                <Icon
                                  sx={{ fontSize: 14, color: '#82949B' }}
                                  className="mr-1.5 flex-shrink-0"
                                />
                                {label}
                              </Button>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 sm:space-y-6">
                    {messages.map((message, index) => {
                      const isStreamingMessage =
                        isLoading &&
                        index === messages.length - 1 &&
                        message.role === 'assistant';

                      return (
                        <ChatMessage
                          key={index}
                          message={message}
                          conversation_id={conversationId}
                          streamingStatus={
                            isStreamingMessage
                              ? streamingStatus || 'Thinking...'
                              : undefined
                          }
                        />
                      );
                    })}

                    <div ref={bottomRef} />
                  </div>
                )}
              </div>
            </ScrollArea>

            {showScrollButton && messages.length > 0 && (
              <Button
                onClick={handleScrollToBottom}
                size="icon"
                className="absolute bottom-4 left-1/2 -translate-x-1/2 h-10 w-10 rounded-full bg-white border border-slate-200 shadow-lg hover:shadow-xl hover:bg-slate-50 transition-all z-10"
                aria-label="Scroll to bottom">
                <ArrowDown className="h-5 w-5 text-[#768D95]" />
              </Button>
            )}
          </div>

          {messages.length > 0 && (
            <div className="bg-[#F9FAFC] flex-shrink-0">
              {error && (
                <div className="max-w-3xl mx-auto w-full px-3 sm:px-4 md:px-6 lg:px-8 pt-2 sm:pt-3">
                  <div className="p-3 sm:p-4 rounded-lg sm:rounded-xl bg-red-50 border border-[#E66B45] flex items-start justify-between gap-3">
                    <p className="text-sm text-[#E66B45] flex-1">{error}</p>
                    <button
                      onClick={() => setError(null)}
                      className="flex-shrink-0 text-[#E66B45] hover:text-[#d15a39] transition-colors"
                      aria-label="Close error message">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
              <div className="max-w-3xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">
                {fileUploads.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {fileUploads.map(uploadState => (
                      <FileUploadItem
                        key={uploadState.id}
                        uploadState={uploadState}
                        onRemove={() => handleRemoveFile(uploadState.id)}
                        disabled={isLoading}
                      />
                    ))}
                  </div>
                )}

                <div className="rounded-2xl bg-white border border-slate-200 shadow-sm focus-within:border-slate-300 focus-within:ring-1 focus-within:ring-slate-200 transition-all overflow-hidden flex flex-col">
                  <div className="flex-1 min-h-[48px]">
                    <Textarea
                      ref={textareaRef}
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Send message to Bernoulli"
                      disabled={isLoading}
                      className="min-h-[48px] max-h-[200px] !border-0 !shadow-none focus-visible:ring-0 bg-transparent text-base font-normal px-4 pt-3 pb-1 rounded-none text-[#161618] placeholder:text-[#C1C6C7] placeholder:text-base placeholder:font-normal resize-none w-full bernoulli-landing-font"
                    />
                  </div>
                  <div className="flex items-center justify-between px-2 pb-2 pt-0 bg-white border-t-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        track(ADP_EVENTS.CHAT_UPLOAD_OPEN, {
                          conversation_id: conversationId || 'none',
                          route: window.location.pathname,
                        });
                        fileInputRef.current?.click();
                      }}
                      disabled={isLoading}
                      className="h-8 w-8 rounded-full bg-[#F9FAFC] hover:bg-[#F3F5F9] text-[#757576] flex-shrink-0 border-0 shadow-none">
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      onClick={isLoading ? handleCancel : handleSendMessage}
                      disabled={isSendDisabled && !isLoading}
                      size="icon"
                      className={`h-8 w-8 rounded-full flex-shrink-0 ml-auto border-0 shadow-none disabled:opacity-100 ${
                        !isSendDisabled
                          ? 'bg-[#7A8C94] hover:bg-[#6A7C84] text-white'
                          : 'bg-[#F9FAFC] hover:bg-[#F3F5F9] text-[#7A8C94]'
                      }`}>
                      {isLoading ? (
                        <X className="h-4 w-4" />
                      ) : (
                        <ArrowUp className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-[#768D95] mt-2 text-center">
                  Bernoulli is AI and can make mistakes. Double-check responses.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
